import { PRIMARY_MODE_TYPES, FALLBACK_MODE_TYPES, DEFAULT_MODE_MIN_PRODUCT, DEFAULT_SECTION_MIN_PRODUCT, DEFAULT_PRIMARY_MODE_TITLE, DEFAULT_FALLBACK_MODE_TITLE } from "./ARSConstants";
import { TSetPriorityMode, TSetModePriorityObject, TCombineModesConfig, TSetModePriority } from "./Types";


export const convertToCombinedMode = (
    modes: TSetPriorityMode[]
): TSetModePriorityObject => {
    const primary: TSetPriorityMode[] = [];
    const fallback: TSetPriorityMode[] = [];

    modes.forEach(mode => {
        const modeWithoutMinProducts = {
            ...mode,
            minNumProducts: undefined
        };

        if (PRIMARY_MODE_TYPES.includes(mode.mode as typeof PRIMARY_MODE_TYPES[number])) {
            primary.push({
                ...modeWithoutMinProducts,
                priority: primary.length + 1
            });
        } else if (FALLBACK_MODE_TYPES.includes(mode.mode as typeof FALLBACK_MODE_TYPES[number])) {
            fallback.push({
                ...modeWithoutMinProducts,
                priority: fallback.length + 1
            });
        }
    });

    return { primary, fallback };
};

export const convertToIndividualMode = (
    modes: TSetModePriorityObject
): TSetPriorityMode[] => {
    const combined = [...modes.primary, ...modes.fallback];

    return combined.map((mode, index) => ({
        ...mode,
        priority: index + 1,
        minNumProducts: mode.minNumProducts ?? DEFAULT_MODE_MIN_PRODUCT
    }));
};

export const getDefaultCombineModesConfig = (enabled: boolean): TCombineModesConfig => {
    if (!enabled) {
        return { enabled: false };
    }

    return {
        enabled: true,
        min_product_count: DEFAULT_SECTION_MIN_PRODUCT,
        primary_modes_min_product_count: DEFAULT_SECTION_MIN_PRODUCT,
        fallback_modes_min_product_count: DEFAULT_SECTION_MIN_PRODUCT,
        max_product_count: 100,
        primary_mode_title: DEFAULT_PRIMARY_MODE_TITLE,
        fallback_mode_title: DEFAULT_FALLBACK_MODE_TITLE
    };
};

export const getPrimaryModes = (
    setModePriority: TSetPriorityMode[] | TSetModePriorityObject
): TSetPriorityMode[] => {
    if (isSetModePriorityObject(setModePriority)) {
        return setModePriority.primary;
    }
    return [];
};

export const getFallbackModes = (
    setModePriority: TSetPriorityMode[] | TSetModePriorityObject
): TSetPriorityMode[] => {
    if (isSetModePriorityObject(setModePriority)) {
        return setModePriority.fallback;
    }
    return [];
};

export const getIndividualModes = (
    setModePriority: TSetPriorityMode[] | TSetModePriorityObject
): TSetPriorityMode[] => {
    if (isSetModePriorityArray(setModePriority)) {
        return setModePriority;
    }
    return [];
};

export const updatePrimaryModesInPriority = (
    currentPriority: TSetModePriorityObject,
    newPrimaryModes: TSetPriorityMode[]
): TSetModePriorityObject => {
    return {
        ...currentPriority,
        primary: newPrimaryModes
    };
};

export const updateFallbackModesInPriority = (
    currentPriority: TSetModePriorityObject,
    newFallbackModes: TSetPriorityMode[]
): TSetModePriorityObject => {
    return {
        ...currentPriority,
        fallback: newFallbackModes
    };
};

export const reorderModes = (
    modes: TSetPriorityMode[],
    sourceIndex: number,
    destinationIndex: number
): TSetPriorityMode[] => {
    const items = Array.from(modes);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    return items.map((item, index) => ({
        ...item,
        priority: index + 1
    }));
};

export const updateModeField = <K extends keyof TSetPriorityMode>(
    modes: TSetPriorityMode[],
    index: number,
    field: K,
    value: TSetPriorityMode[K]
): TSetPriorityMode[] => {
    const updatedModes = [...modes];
    updatedModes[index] = {
        ...updatedModes[index],
        [field]: value
    };
    return updatedModes;
};

export const validateModeTitles = (
    setModePriority: TSetPriorityMode[] | TSetModePriorityObject,
    combineModesConfig: TCombineModesConfig
): boolean => {
    if (combineModesConfig.enabled && isSetModePriorityObject(setModePriority)) {
        const primaryTitleValid = (combineModesConfig.primary_mode_title?.trim().length ?? 0) > 0;
        const fallbackTitleValid = (combineModesConfig.fallback_mode_title?.trim().length ?? 0) > 0;
        return primaryTitleValid && fallbackTitleValid;
    }

    if (isSetModePriorityArray(setModePriority)) {
        return setModePriority.every(mode => mode.title?.trim().length > 0);
    }

    return true;
};

export const validateMinProductCount = (
    setModePriority: TSetPriorityMode[] | TSetModePriorityObject,
    combineModesConfig: TCombineModesConfig
): boolean => {
    if (combineModesConfig.enabled) {
        const primaryMinValid = (combineModesConfig.primary_modes_min_product_count ?? 0) > 0;
        const fallbackMinValid = (combineModesConfig.fallback_modes_min_product_count ?? 0) > 0;
        return primaryMinValid && fallbackMinValid;
    }

    if (isSetModePriorityArray(setModePriority)) {
        return setModePriority.every(mode => (mode.minNumProducts ?? 0) > 0);
    }

    return true;
};

export const isSetModePriorityObject = (
    priority: TSetModePriority
): priority is TSetModePriorityObject => {
    return (
        priority !== null &&
        typeof priority === 'object' &&
        !Array.isArray(priority) &&
        'primary' in priority &&
        'fallback' in priority
    );
};

export const isSetModePriorityArray = (
    priority: TSetModePriority
): priority is TSetPriorityMode[] => {
    return Array.isArray(priority);
};

export const isCombinedModeEnabled = (config: TCombineModesConfig | undefined): boolean => {
    return config?.enabled ?? false;
};

export const hasValidModeTitles = (
    submodels: { setModePriority: TSetPriorityMode[] | TSetModePriorityObject; combine_modes_config?: TCombineModesConfig }[]
): boolean => {
    return submodels.every((submodel) =>
        validateModeTitles(submodel.setModePriority, submodel.combine_modes_config ?? { enabled: false })
    );
};

export const hasValidMinProductCounts = (
    submodels: { setModePriority: TSetPriorityMode[] | TSetModePriorityObject; combine_modes_config?: TCombineModesConfig }[]
): boolean => {
    return submodels.every((submodel) =>
        validateMinProductCount(submodel.setModePriority, submodel.combine_modes_config ?? { enabled: false })
    );
};